import { isValidEmail } from "src/utils/regex";
import z from "zod";

const emailRecipients = z.enum([
	"regional-manager",
	"area-manager",
	"store-manager",
	"observed-individual",
	"observer",
	"specific-email"
]);

const schema = z.object({
	formName: z.string().min(1, { message: "Form name is required" }),

	category: z.number().optional(),

	observationType: z.enum([
		"",
		"INDIVIDUAL",
		"STORE_SCORED_ASSESSMENT",
		"STORE_SELF_ASSESSMENT",
		"GREAT_CONVERSATION"
	]),

	sendEmail: z.boolean(),
	limitToCurrentUser: z.boolean(),
	observationRecipients: z.array(emailRecipients).nullable(),
	gradingRecipients: z.array(emailRecipients).nullable(),
	observationSpecificEmailAddress: z.string().nullable(),
	gradingSpecificEmailAddress: z.string().nullable(),

	customEmail: z.boolean(),
	customEmailTitle: z.string().nullable(),
	customEmailSubject: z.string().nullable(),
	customEmailBody: z.string().nullable()
});

type ISchema = z.infer<typeof schema>;

const refineObservationType = ({ observationType }: ISchema) => {
	if (!observationType) return false;

	return true;
};

const refineObservationEmailAddress = ({
	observationRecipients,
	observationSpecificEmailAddress
}: ISchema) => {
	if (
		observationRecipients?.includes("specific-email") &&
		(!observationSpecificEmailAddress ||
			!isValidEmail(observationSpecificEmailAddress))
	) {
		return false;
	}

	return true;
};

const refineGradingEmailAddress = ({
	gradingRecipients,
	gradingSpecificEmailAddress
}: ISchema) => {
	if (
		gradingRecipients?.includes("specific-email") &&
		(!gradingSpecificEmailAddress || !isValidEmail(gradingSpecificEmailAddress))
	) {
		return false;
	}

	return true;
};

const refineGradingRecipients = ({
	observationType,
	gradingRecipients,
	sendEmail
}: ISchema) => {
	if (
		observationType === "GREAT_CONVERSATION" &&
		sendEmail &&
		gradingRecipients?.length === 0
	) {
		return false;
	}

	return true;
};

const refineObservationRecipients = ({
	sendEmail,
	observationRecipients
}: ISchema) => {
	if (sendEmail && observationRecipients?.length === 0) {
		return false;
	}

	return true;
};

const refineCustomEmailTitle = ({ customEmail, customEmailTitle }: ISchema) => {
	if (customEmail && customEmailTitle?.length === 0) {
		return false;
	}

	return true;
};

const refineCustomEmailSubject = ({
	customEmail,
	customEmailSubject
}: ISchema) => {
	if (customEmail && customEmailSubject?.length === 0) {
		return false;
	}

	return true;
};

const refineCustomEmailBody = ({ customEmail, customEmailBody }: ISchema) => {
	if (customEmail && customEmailBody?.length === 0) {
		return false;
	}

	return true;
};

const validationSchema = schema
	.refine(refineObservationType, {
		message: "Form type is required",
		path: ["observationType"]
	})
	.refine(refineObservationEmailAddress, {
		message: "Enter a valid email address for observations",
		path: ["observationSpecificEmailAddress"]
	})
	.refine(refineGradingEmailAddress, {
		message: "Enter a valid email address for grading",
		path: ["gradingSpecificEmailAddress"]
	})
	.refine(refineObservationRecipients, {
		message:
			"At least one option must be selected to receive observation results",
		path: ["observationRecipients"]
	})
	.refine(refineGradingRecipients, {
		message: "At least one option must be selected to receive grading results",
		path: ["gradingRecipients"]
	})
	.refine(refineCustomEmailTitle, {
		message: "Email title is required",
		path: ["customEmailTitle"]
	})
	.refine(refineCustomEmailSubject, {
		message: "Email subject is required",
		path: ["customEmailSubject"]
	})
	.refine(refineCustomEmailBody, {
		message: "Intro text is required",
		path: ["customEmailBody"]
	});

type Schema = z.infer<typeof validationSchema>;

export { validationSchema as schema, type Schema };
